import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import type { Category, ColorEnum } from '../api/categories';
import { useCategories, useReorderCategories } from '../api/categories';
import { useEvent } from '../api/events';
import type { InventoryStateRow } from '../api/inventory';
import { useInventoryState } from '../api/inventory';
import type { Product } from '../api/products';
import { useProducts, useReorderProducts } from '../api/products';
import { DragList } from '../components/DragList';
import { I } from '../components/Icons';
import { LastUpdated } from '../components/LastUpdated';
import { MenuButton } from '../components/MenuButton';
import { CategoryModal } from './CategoryModal';
import { InventoryHistoryDrawer } from './InventoryHistoryDrawer';
import { InventoryRestockModal } from './InventoryRestockModal';
import { ProductModal } from './ProductModal';

const COLOR_VAR: Record<ColorEnum, string> = {
  CORAL: 'var(--oeg-coral)',
  TEAL: 'var(--oeg-teal)',
  GREEN: 'var(--oeg-green)',
  LILAC: 'var(--oeg-lilac)',
  PURPLE: 'var(--oeg-purple)',
};

// Shared grid template for the products table header and rows so columns
// stay aligned. Columns: grip, thumb, name+sku, stock, price, kebab.
// Category is shown via section headers, not a per-row cell.
const PRODUCTS_GRID = '28px 60px 1.4fr 120px 100px 44px';

const LOW_AT_KEY = 'catalog:lowAt';

function stockColor(row: InventoryStateRow, lowAt: number): string {
  const remaining = row.inventoryRemaining ?? 0;
  const starting = row.inventoryStarting ?? 0;
  if (remaining <= 0) return 'var(--oeg-danger)';
  if (remaining < lowAt || (starting > 0 && remaining < starting * 0.2)) {
    return 'var(--oeg-warning, #B8860B)';
  }
  return 'var(--oeg-success)';
}

interface CatChipProps {
  label: string;
  count: number;
  color?: ColorEnum;
  active: boolean;
  draggable: boolean;
  onClick: () => void;
  onEdit?: () => void;
}

function CatChip({ label, count, color, active, onClick, onEdit }: CatChipProps) {
  return (
    <span
      className="cat-chip-wrap"
      style={{ position: 'relative', display: 'inline-flex' }}
    >
      <button
        type="button"
        className="cat-chip"
        data-active={active ? 'true' : 'false'}
        onClick={onClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '7px 14px',
          borderRadius: 999,
          background: active ? 'var(--oeg-purple)' : 'transparent',
          color: active ? '#fff' : 'var(--fg-1)',
          border: '1px solid ' + (active ? 'var(--oeg-purple)' : 'var(--border-1)'),
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          cursor: 'pointer',
        }}
      >
        {color && (
          <span
            aria-hidden="true"
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: COLOR_VAR[color],
              flexShrink: 0,
            }}
          />
        )}
        {label}
        <span style={{ opacity: 0.6, fontWeight: 500 }}>{count}</span>
      </button>
      {onEdit && (
        <button
          type="button"
          aria-label={`Edit ${label}`}
          className="cat-chip-edit"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: -6,
            right: -6,
            width: 22,
            height: 22,
            borderRadius: 999,
            border: '1px solid var(--border-1)',
            background: 'var(--bg-surface)',
            color: 'var(--fg-2)',
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
          }}
        >
          <I.edit size={12} />
        </button>
      )}
    </span>
  );
}

export function Catalog() {
  const { eventId = '' } = useParams();
  const [activeCatId, setActiveCatId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [trackedOnly, setTrackedOnly] = useState<boolean>(false);
  const [showArchived, setShowArchived] = useState<boolean>(false);

  const eventQuery = useEvent(eventId);
  const categoriesQuery = useCategories(eventId);
  const productsQuery = useProducts(eventId, { includeArchived: showArchived });
  const inventoryQuery = useInventoryState(eventId);
  const reorderCategories = useReorderCategories(eventId);
  const reorderProducts = useReorderProducts(eventId);
  const [lowAt, setLowAt] = useState<number>(() => {
    if (typeof window === 'undefined') return 0;
    const v = window.localStorage.getItem(LOW_AT_KEY);
    if (!v) return 0;
    const n = Number.parseInt(v, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(LOW_AT_KEY, String(lowAt));
  }, [lowAt]);

  const [categoryModal, setCategoryModal] = useState<
    { mode: 'create' | 'edit'; category: Category | null } | null
  >(null);
  const [productModal, setProductModal] = useState<
    { mode: 'create' | 'edit'; product: Product | null } | null
  >(null);
  const [openInvModal, setOpenInvModal] = useState<
    { row: InventoryStateRow; mode: 'restock' | 'adjust' } | null
  >(null);
  const [openHistory, setOpenHistory] = useState<InventoryStateRow | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const allCategories = categoriesQuery.data ?? [];
  const activeCategories = useMemo(
    () =>
      allCategories
        .filter((c) => !c.archivedAt)
        .slice()
        .sort((a, b) => a.sort - b.sort),
    [allCategories],
  );
  const allProducts = productsQuery.data ?? [];
  const sortProducts = (list: Product[]) =>
    list.slice().sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name));
  // NOTE: the `!p.archivedAt` filter here is load-bearing, not redundant. When
  // "Show archived" is on the backend returns archived rows too, and these must
  // stay out of chip counts and the reorder payload (handleProductsReorder).
  const activeProducts = useMemo(
    () => sortProducts(allProducts.filter((p) => !p.archivedAt)),
    [allProducts],
  );
  // What the table renders: active-only, or the full list when showing archived.
  const displayProducts = useMemo(
    () => (showArchived ? sortProducts(allProducts) : activeProducts),
    [showArchived, allProducts, activeProducts],
  );
  const activeCatIdSet = useMemo(
    () => new Set(activeCategories.map((c) => c.id)),
    [activeCategories],
  );

  const inventoryById = useMemo(() => {
    const m = new Map<string, InventoryStateRow>();
    for (const r of inventoryQuery.data ?? []) m.set(r.productId, r);
    return m;
  }, [inventoryQuery.data]);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return displayProducts.filter((p) => {
      if (activeCatId && p.categoryId !== activeCatId) return false;
      if (trackedOnly) {
        const inv = inventoryById.get(p.id);
        if (!inv?.inventoryTracked) return false;
      }
      if (!q) return true;
      const name = p.name.toLowerCase();
      const sku = (p.sku ?? '').toLowerCase();
      return name.includes(q) || sku.includes(q);
    });
  }, [displayProducts, activeCatId, search, trackedOnly, inventoryById]);

  const currency = eventQuery.data?.currency ?? 'USD';
  const formatPrice = useMemo(() => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency });
    } catch {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    }
  }, [currency]);

  // Group filteredProducts by category, in the same order as the chips.
  // Sections with zero visible products (after filters) are hidden.
  const visibleSections = useMemo(() => {
    const byCat = new Map<string, Product[]>();
    for (const p of filteredProducts) {
      const list = byCat.get(p.categoryId);
      if (list) list.push(p);
      else byCat.set(p.categoryId, [p]);
    }
    const sections: { cat: Category; products: Product[] }[] = [];
    for (const cat of activeCategories) {
      if (activeCatId !== null && activeCatId !== cat.id) continue;
      const products = byCat.get(cat.id) ?? [];
      if (products.length === 0) continue;
      sections.push({ cat, products });
    }
    return sections;
  }, [filteredProducts, activeCategories, activeCatId]);

  // Catch-all for products whose category isn't in the active set (e.g. an
  // archived product under an archived category). Only surfaced when showing
  // archived and not filtering to a specific category chip.
  const orphanProducts = useMemo(() => {
    if (!showArchived || activeCatId !== null) return [];
    return filteredProducts.filter((p) => !activeCatIdSet.has(p.categoryId));
  }, [showArchived, activeCatId, filteredProducts, activeCatIdSet]);

  function handleCategoriesReorder(ids: string[]) {
    void reorderCategories.mutateAsync(ids).catch(() => {
      // optimistic mutation rolls back on error
    });
  }

  function handleProductsReorder(reorderedCatId: string, newVisibleIds: string[]) {
    // Drag is constrained to a single category by giving each category its
    // own DragList/SortableContext. To send the full ordering required by the
    // reorder endpoint, splice the new within-category order back into the
    // overall product list while preserving the relative order of every other
    // category and any orphan products.
    const visibleSet = new Set(newVisibleIds);
    const fullOrder: string[] = [];
    const knownCatIds = new Set(activeCategories.map((c) => c.id));
    for (const cat of activeCategories) {
      const catProducts = activeProducts.filter((p) => p.categoryId === cat.id);
      if (cat.id === reorderedCatId) {
        fullOrder.push(...newVisibleIds);
        for (const p of catProducts) {
          if (!visibleSet.has(p.id)) fullOrder.push(p.id);
        }
      } else {
        for (const p of catProducts) fullOrder.push(p.id);
      }
    }
    for (const p of activeProducts) {
      if (!knownCatIds.has(p.categoryId)) fullOrder.push(p.id);
    }
    void reorderProducts.mutateAsync(fullOrder).catch(() => {
      // optimistic mutation rolls back on error
    });
  }

  function renderProductRow(p: Product) {
    const inv = inventoryById.get(p.id);
    const tracked = inv?.inventoryTracked ?? false;
    const archived = Boolean(p.archivedAt);
    return (
      <div
        className="product-row"
        data-testid={`product-row-${p.id}`}
        onClick={() => setProductModal({ mode: 'edit', product: p })}
        style={{
          display: 'grid',
          gridTemplateColumns: PRODUCTS_GRID,
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid var(--border-1)',
          cursor: 'pointer',
          fontSize: 13,
          opacity: archived ? 0.55 : 1,
        }}
      >
        {showArchived ? (
          // Reordering is disabled in the archived view, so render an inert
          // placeholder instead of a misleading grab handle.
          <div />
        ) : (
          <div
            aria-label="Drag handle"
            className="drag-handle"
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'grid',
              placeItems: 'center',
              color: 'var(--fg-4)',
              cursor: 'grab',
            }}
          >
            <I.grip size={16} />
          </div>
        )}
        <div
          aria-hidden="true"
          style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            background: COLOR_VAR[p.thumbColor],
            opacity: 0.85,
            display: 'grid',
            placeItems: 'center',
            color: '#2E1A48',
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: 11,
          }}
        >
          {p.thumbLabel}
        </div>
        <div>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              color: 'var(--fg-1)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {p.name}
            {archived && (
              <span
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: 9,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'var(--fg-3)',
                  border: '1px solid var(--border-1)',
                  borderRadius: 999,
                  padding: '1px 7px',
                }}
              >
                Archived
              </span>
            )}
          </div>
          {p.sku && (
            <div
              className="mono"
              style={{ color: 'var(--fg-3)', fontSize: 11, marginTop: 2 }}
            >
              {p.sku}
            </div>
          )}
        </div>
        <div
          className="num"
          style={{
            textAlign: 'right',
            fontFamily: 'var(--font-display)',
            fontWeight: tracked ? 700 : 400,
            color: tracked && inv ? stockColor(inv, lowAt) : 'var(--fg-3)',
          }}
        >
          {tracked && inv ? `${inv.inventoryRemaining ?? 0} left` : '—'}
        </div>
        <div
          className="num"
          style={{
            textAlign: 'right',
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
          }}
        >
          {formatPrice.format(p.priceCents / 100)}
        </div>
        <div style={{ display: 'grid', placeItems: 'center' }}>
          {tracked && inv && (
            <MenuButton
              open={openMenuId === p.id}
              onOpenChange={(open) => setOpenMenuId(open ? p.id : null)}
              ariaLabel={`Inventory actions for ${p.name}`}
              items={[
                {
                  label: 'Restock',
                  onClick: () => setOpenInvModal({ row: inv, mode: 'restock' }),
                },
                {
                  label: 'Adjust',
                  onClick: () => setOpenInvModal({ row: inv, mode: 'adjust' }),
                },
                {
                  label: 'History',
                  onClick: () => setOpenHistory(inv),
                },
              ]}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="card-pad">
      <div
        className="page-head"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 16,
          gap: 16,
        }}
      >
        <div>
          <h1>Catalog</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setCategoryModal({ mode: 'create', category: null })}
          >
            <I.tag size={14} /> New category
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() =>
              setProductModal({ mode: 'create', product: null })
            }
          >
            <I.plus size={14} /> New product
          </button>
        </div>
      </div>

      <div className="card">
        <div
          style={{
            padding: 12,
            borderBottom: '1px solid var(--border-1)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <CatChip
            label="All"
            count={activeProducts.length}
            active={activeCatId === null}
            draggable={false}
            onClick={() => setActiveCatId(null)}
          />
          <DragList
            items={activeCategories}
            onReorder={handleCategoriesReorder}
            direction="horizontal"
          >
            {(c) => (
              <CatChip
                label={c.name}
                count={activeProducts.filter((p) => p.categoryId === c.id).length}
                color={c.color}
                active={activeCatId === c.id}
                draggable
                onClick={() => setActiveCatId(activeCatId === c.id ? null : c.id)}
                onEdit={() => setCategoryModal({ mode: 'edit', category: c })}
              />
            )}
          </DragList>
          <div style={{ flex: 1 }} />
          <div className="search">
            <I.search />
            <input
              type="search"
              aria-label="Search products"
              placeholder="Search products, SKU…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <label
            style={{
              display: 'inline-flex',
              gap: 6,
              alignItems: 'center',
              fontSize: 13,
              color: 'var(--fg-2)',
            }}
          >
            <input
              type="checkbox"
              checked={trackedOnly}
              onChange={(e) => setTrackedOnly(e.target.checked)}
            />
            Tracked only
          </label>
          <label
            style={{
              display: 'inline-flex',
              gap: 6,
              alignItems: 'center',
              fontSize: 13,
              color: 'var(--fg-2)',
            }}
          >
            <input
              type="checkbox"
              aria-label="Show archived products"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show archived
          </label>
          <label
            style={{
              display: 'inline-flex',
              gap: 6,
              alignItems: 'center',
              fontSize: 13,
              color: 'var(--fg-2)',
            }}
          >
            Low at
            <input
              type="number"
              min={0}
              step={1}
              value={lowAt}
              onChange={(e) =>
                setLowAt(Math.max(0, Number.parseInt(e.target.value || '0', 10) || 0))
              }
              style={{ width: 56 }}
            />
          </label>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label="Refresh inventory"
            onClick={() => void inventoryQuery.refetch()}
          >
            <I.refresh size={14} />
          </button>
        </div>

        <div className="products-table">
          <div
            className="products-header"
            style={{
              display: 'grid',
              gridTemplateColumns: PRODUCTS_GRID,
              alignItems: 'center',
              padding: '12px 16px',
              background: 'var(--bg-surface-2)',
              borderBottom: '1px solid var(--border-1)',
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              color: 'var(--fg-3)',
            }}
          >
            <div />
            <div />
            <div>Name</div>
            <div className="num" style={{ textAlign: 'right' }}>Stock</div>
            <div className="num" style={{ textAlign: 'right' }}>Price</div>
            <div />
          </div>
          {visibleSections.length === 0 && orphanProducts.length === 0 && (
            <div
              className="empty-row"
              style={{
                padding: '24px 16px',
                textAlign: 'center',
                color: 'var(--fg-3)',
                fontSize: 13,
              }}
            >
              {displayProducts.length === 0
                ? 'No products yet. Create your first one.'
                : 'No products match your filters.'}
            </div>
          )}
        </div>

        {visibleSections.map(({ cat, products }) => (
          <div key={cat.id}>
            <div
              style={{
                padding: '10px 16px',
                background: 'var(--bg-surface-2)',
                borderBottom: '1px solid var(--border-1)',
                borderTop: '1px solid var(--border-1)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: COLOR_VAR[cat.color],
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  color: 'var(--fg-2)',
                }}
              >
                {cat.name}
              </span>
              <span style={{ color: 'var(--fg-4)', fontSize: 11 }}>
                · {products.length}
              </span>
            </div>
            <DragList
              items={products}
              onReorder={(ids) => handleProductsReorder(cat.id, ids)}
              direction="vertical"
              disabled={showArchived}
            >
              {(p) => renderProductRow(p)}
            </DragList>
          </div>
        ))}

        {orphanProducts.length > 0 && (
          <div>
            <div
              style={{
                padding: '10px 16px',
                background: 'var(--bg-surface-2)',
                borderBottom: '1px solid var(--border-1)',
                borderTop: '1px solid var(--border-1)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  color: 'var(--fg-2)',
                }}
              >
                Archived / removed categories
              </span>
              <span style={{ color: 'var(--fg-4)', fontSize: 11 }}>
                · {orphanProducts.length}
              </span>
            </div>
            <DragList items={orphanProducts} onReorder={() => {}} direction="vertical" disabled>
              {(p) => renderProductRow(p)}
            </DragList>
          </div>
        )}
      </div>

      <LastUpdated dataUpdatedAt={inventoryQuery.dataUpdatedAt ?? 0} />

      {categoryModal && (
        <CategoryModal
          eventId={eventId}
          mode={categoryModal.mode}
          category={categoryModal.category}
          onClose={() => setCategoryModal(null)}
        />
      )}
      {productModal && (
        <ProductModal
          eventId={eventId}
          mode={productModal.mode}
          product={productModal.product}
          defaultCategoryId={productModal.mode === 'create' ? activeCatId : null}
          startingEditable={
            productModal.product
              ? (inventoryById.get(productModal.product.id)?.startingEditable ?? true)
              : true
          }
          onClose={() => setProductModal(null)}
        />
      )}
      {openInvModal && (
        <InventoryRestockModal
          eventId={eventId}
          product={openInvModal.row}
          mode={openInvModal.mode}
          onClose={() => setOpenInvModal(null)}
        />
      )}
      {openHistory && (
        <InventoryHistoryDrawer
          eventId={eventId}
          product={openHistory}
          onClose={() => setOpenHistory(null)}
        />
      )}
    </div>
  );
}
